import { useEffect } from "react";

/**
 * FAQ Toggle Card CSS Styles
 * Used in Head tag for proper styling of toggle cards
 */
export const FAQ_TOGGLE_STYLES = `
  .kg-card.kg-toggle-card {
    background-color: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    padding: 0px 10px;
    
    display: flex;
    flex-direction: column;
    transition: box-shadow 0.3s ease;
  }

  .kg-card.kg-toggle-card:hover {
    box-shadow: 0 0 15px rgba(0, 0, 0, 0.1);
  }

  .kg-toggle-heading {
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
  }

  .kg-toggle-heading-text {
    font-size: 1.125rem;
    font-weight: 600;
    color: #007bff;
    margin-bottom: 10px;
  }

  .kg-toggle-card-icon {
    width: 24px;
    height: 24px;
    transition: transform 0.3s ease, fill 0.3s ease;
    cursor: pointer;
  }

  .kg-toggle-card-icon svg {
    fill: #15171a;
  }

  .kg-toggle-card-icon:hover {
    transform: rotate(180deg);
  }

  .kg-toggle-content {
    display: none;
    margin-top: 16px;
    font-size: 1rem;
    color: #333;
    padding: 0 20px;
  }

  [data-kg-toggle-state="open"] .kg-toggle-content {
    display: block;
  }

  .kg-toggle-card[data-kg-toggle-state="open"] {
    background-color: #f9f9f9;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  .kg-toggle-content p {
    line-height: 1.6;
  }

  @media (max-width: 768px) {
    .kg-toggle-heading {
      flex-direction: column;
      align-items: flex-start;
    }

    .kg-toggle-heading-text {
      margin-bottom: 8px;
    }

    .kg-toggle-card-icon {
      margin-top: 10px;
    }
  }
`;

/**
 * useFAQToggleHandler Hook
 * Handles toggle card (FAQ) click functionality with event delegation
 * CSS styles exported as FAQ_TOGGLE_STYLES constant for use in Head tags
 * 
 * Usage: useFAQToggleHandler([fetchedCollege?.id])
 */
export const useFAQToggleHandler = (dependencies = []) => {
  useEffect(() => {
    // Delegate click handling to document body for proper event delegation
    const handleToggleClick = (event) => {
      const card = event.target.closest(".kg-toggle-card");
      if (!card) return;

      const isOpen = card.getAttribute("data-kg-toggle-state") === "open";
      
      // Close all other open FAQs
      const allCards = document.querySelectorAll(".kg-toggle-card");
      allCards.forEach((otherCard) => {
        if (otherCard !== card) {
          otherCard.setAttribute("data-kg-toggle-state", "close");
        }
      });

      // Toggle current card
      card.setAttribute("data-kg-toggle-state", isOpen ? "close" : "open");
      event.preventDefault();
      event.stopPropagation();
    };

    const styleToggleCards = () => {
      const toggleCards = document.querySelectorAll(".kg-toggle-card");
      
      toggleCards.forEach((card) => {
        // Apply inline styles for cursor and layout
        card.style.maxWidth = "100%";
        card.style.boxSizing = "border-box";
        card.style.cursor = "pointer";

        const heading = card.querySelector(".kg-toggle-heading");
        if (heading) {
          heading.style.cursor = "pointer";
        }

        const button = card.querySelector(".kg-toggle-card-icon");
        if (button) {
          button.style.cursor = "pointer";
        }
      });
    };

    // Attach event listener to document body using event delegation
    document.body.addEventListener("click", handleToggleClick, false);

    // Initial styling after a short delay to ensure DOM is ready
    const initialTimeout = setTimeout(() => {
      styleToggleCards();
    }, 50);

    // Set up MutationObserver for dynamically added content
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length) {
          // Apply styles to newly added toggle cards
          styleToggleCards();
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: false,
    });

    // Cleanup function
    return () => {
      clearTimeout(initialTimeout);
      document.body.removeEventListener("click", handleToggleClick, false);
      observer.disconnect();
    };
  }, dependencies);
};
